<template>
  <div
    style="position: relative; width: 100%;"
  >
    <h2
      class="mb-3"
    >{{ title }}</h2>

    <div
      class="mr-4"
      style="position: absolute; top: 0; right: 0;"
    >
      <v-btn
        outlined
        color="foreground"
        small
        @click="showHeaders = !showHeaders;"
      >Headers</v-btn>

      <div
        v-if="showHeaders"
        class="d-flex flex-column pa-3"
        :style="`position: fixed; top: 64px; right: 0px; background-color: white; border: 1px solid #ccc; z-index: 1000; width: 200px; box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);`"
      >
        <v-btn
          outlined
          color="red"
          small
          @click="showHeaders = false;"
        >Close</v-btn>

        <div
          v-for="(header, i) in headersLocal"
          :key="'header-' + i"
          class="d-flex align-center"
        >
          <div
            v-if="!isHidden(header) && headerValue(header) !== '__actions'"
            class="d-flex align-center"
            style="width: 100%;"
          >
            <v-checkbox
              v-model="header.show"
              color="primary"
              hide-details
              dense
              class="mr-2"
              @change="updateShowHeader(header, $event)"
            ></v-checkbox>
            <span>{{ header.text }}</span>
          </div>
        </div>
      </div>
    </div>

    <div
      class="d-flex align-center justify-space-between"
    >
      <div
        style="width: 100%;"
      >
        <v-text-field
          v-if="allowSearch"
          v-model="search"
          :placeholder="`Search through ${totalItems} rows. Press Enter to search.`"
          prepend-inner-icon="mdi-magnify"
          outlined
          hide-details
          single-line
          dense
          :disabled="disabled"
          class="mr-2"
          @keyup="searchKey($event)"
        ></v-text-field>
      </div>

      <div
        class="d-flex align-center"
      >
        <slot-chip
          v-if="allowAdd && (permissions & crudPerms) === crudPerms"
          color="blue"
          tooltip="Add Row"
          :disabled="disabled"
          @click="insertItemDialog()"
        >
          <v-icon>mdi-plus</v-icon>
        </slot-chip>

        <slot-chip
          v-for="(button, i) in customButtons"
          :key="'custom-button-' + i"
          :color="button.color"
          :tooltip="button.tooltip"
          :disabled="disabled"
          :light="button.light"
          :dark="button.dark"
          @click="$emit('custom-toolbar-button', button.action)"
        >
          <v-icon
            v-if="button.icon"
            :color="button.light ? 'black' : 'white'"
          >{{ button.icon }}</v-icon>
        </slot-chip>

        <slot-chip
          v-if="allowDownload"
          color="orange"
          tooltip="Download Table"
          :disabled="disabled"
          @click="download(itemsFiltered);"
        >
          <v-icon>mdi-download</v-icon>
        </slot-chip>

        <slot-chip
          v-if="allowDownload"
          color="orange"
          tooltip="Copy Table"
          :disabled="disabled"
          @click="copy(itemsFiltered);"
        >
          <v-icon>mdi-content-copy</v-icon>
        </slot-chip>

        <slot-chip
          v-if="allowRefresh"
          color="blue"
          tooltip="Refresh Table"
          :disabled="disabled"
          :loading="loading"
          @click="$emit('refresh');"
        >
          <v-icon>mdi-refresh</v-icon>
        </slot-chip>
      </div>
    </div>

    <div
      style="width: 100%; overflow-x: auto;"
    >

      <table
        class="__data-table mt-2 mb-2 mx-auto"
        :style="`width: 100%; ${disabled ? 'opacity: 0.5;' : ''}; overflow-x: auto;`"
        :key="tableKey"
      >

        <thead>
          <tr>
            <th
              v-for="(header, i) in headersLocal"
              v-if="isHidden(header) === false && header.show"
              :key="'head-' + i"
              :style="`user-select: none; min-width: ${headerWidth(header)}; overflow-x: auto;`"
            >
              {{ header.disabled }}
              <div
                v-if="allowSort"
                class="text-center"
                style="width: 100%; cursor: pointer;"
                @click="setSort(header)"
              >
                {{ headerText(header) }}

                <v-icon
                  v-if="sortBy.find(f => f.sortBy === headerValue(header)) && sortBy.find(f => f.sortBy === headerValue(header)).sortDir === 'desc'"
                  color="foreground"
                  small
                >mdi-arrow-down</v-icon>

                <v-icon
                  v-else-if="sortBy.find(f => f.sortBy === headerValue(header)) && sortBy.find(f => f.sortBy === headerValue(header)).sortDir === 'asc'"
                  color="foreground"
                  small
                >mdi-arrow-up</v-icon>
              </div>

              <div
                v-else
                class="text-center"
                style="width: 100%; cursor: pointer;"
              >
                {{ headerText(header) }}
              </div>
            </th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-if="loading || internalLoading"
          >
            <td
              colspan="100%"
              style="padding: 0 !important; min-height: 0 !important; height: auto !important;"
            >
              <v-progress-linear
                :active="loading || internalLoading"
                color="primary"
                indeterminate
                style="width: 100%; padding: 0 !important;"
              ></v-progress-linear>
            </td>
          </tr>

          <tr
            v-for="(item, i) in itemsShow"
            :key="'itemrow-' + i"
          >

            <td
              v-for="(header, j) in headersLocal"
              v-if="isHidden(header) === false && header.show"
              :key="'itemrowcol-' + i + '-' + headerValue(header)"
              class="py-1"
              :style="`min-width: ${headerWidth(header)}; overflow-x: auto;`"
            >
              <!-- Actions -->
              <div
                v-if="actionsActive && headerValue(header) === '__actions'"
                class="d-flex justify-center align-center"
              >

                <slot-chip
                  v-for="(action, k) in customActions"
                  :key="'custom-item-action-' + k"
                  :disabled="disabled"
                  color="foreground"
                  :size="24"
                  text
                  @click="$emit('custom-item-action', { id: action.id, item: itemsShow[i] });"
                >
                  <v-icon
                    :color="action.color"
                    style="font-size: 20px;"
                  >{{ action.icon }}</v-icon>
                </slot-chip>

                <slot-chip
                  v-if="allowEdit && (permissions & crudPerms) === crudPerms"
                  :disabled="disabled"
                  color="foreground"
                  tooltip="Edit Row"
                  :size="24"
                  text
                  @click="updateItemDialog($event, item)"
                >
                  <v-icon
                    color="blue"
                    style="font-size: 20px;"
                  >mdi-pencil</v-icon>
                </slot-chip>

                <slot-chip
                  v-if="allowRefresh"
                  :disabled="disabled"
                  color="foreground"
                  tooltip="Refresh Row"
                  :size="24"
                  text
                  @click="$emit('refresh-item', transformFromTable(item));"
                >
                  <v-icon
                    color="blue"
                    style="font-size: 20px;"
                  >mdi-refresh</v-icon>
                </slot-chip>

                <slot-chip
                  v-if="allowDelete && (permissions & crudPerms) === crudPerms"
                  :disabled="disabled"
                  color="foreground"
                  tooltip="Delete Row"
                  :size="24"
                  text
                  @click="itemDelete = itemsShow[i]; dialogDelete = true;"
                >
                  <v-icon
                    color="red"
                    style="font-size: 20px;"
                  >mdi-trash-can</v-icon>
                </slot-chip>
              </div>

              <!-- Edit -->
              <div
                v-else-if="item['__data-table-edit'] === headerValue(header)"
              >
                <!-- Select -->
                <v-select
                  v-if="itemTemplate[headerValue(header)].type === 'select'"
                  :id="'edit-' + i + '-' + headerValue(header)"
                  v-model="itemsEdit[i][headerValue(header)]"
                  :items="itemTemplate[headerValue(header)].items || []"
                  :item-text="itemTemplateItemText(header)"
                  :item-value="itemTemplateItemValue(header)"
                  :multiple="itemTemplate[headerValue(header)].multiple"
                  outlined
                  dense
                  hide-details
                  class="__data-table-input"
                  @keydown.enter="updateItem(i, item);"
                  @blur="updateItem(i, item);"
                ></v-select>

                <!-- Autocomplete -->
                <v-autocomplete
                  v-else-if="itemTemplate[headerValue(header)].type === 'autocomplete'"
                  :id="'edit-' + i + '-' + headerValue(header)"
                  v-model="itemsEdit[i][headerValue(header)]"
                  :items="itemTemplate[headerValue(header)].items || []"
                  :item-text="itemTemplateItemText(header)"
                  :item-value="itemTemplateItemValue(header)"
                  :multiple="itemTemplate[headerValue(header)].multiple"
                  outlined
                  dense
                  hide-details
                  class="__data-table-input"
                  @keydown.enter="updateItem(i, item);"
                  @blur="updateItem(i, item);"
                ></v-autocomplete>

                <!-- Default -->
                <v-text-field
                  v-else
                  :id="'edit-' + i + '-' + headerValue(header)"
                  v-model="itemsEdit[i][headerValue(header)]"
                  :type="itemTemplate[headerValue(header)].type === 'currency' ? 'number' : itemTemplate[headerValue(header)].type"
                  :step="itemTemplate[headerValue(header)].step"
                  outlined
                  dense
                  hide-details
                  :class="`__data-table-input${itemTemplate[headerValue(header)].hideArrows ? ' __data-table-input-hide-arrows' : ''}`"
                  @keydown.enter="updateItem(i, item);"
                  @blur="updateItem(i, item);"
                ></v-text-field>
              </div>

              <!-- Display -->
              <div
                v-else
              >
                <!-- Currency -->
                <div
                  v-if="itemTemplate[headerValue(header)].type === 'currency'"
                  @dblclick="allowDblClickEdit ? editItem(i, headerValue(header)): () => {}"
                  :class="headerAlign(header)"
                  :style="allowEdit && allowDblClickEdit ? `user-select: none; cursor: pointer;` : ``"
                >
                  {{ item[headerValue(header)] != null ? USDollar.format(item[headerValue(header)]) : '-' }}
                </div>

                <!-- Number -->
                <div
                  v-else-if="itemTemplate[headerValue(header)].type === 'number'"
                  @dblclick="allowDblClickEdit ? editItem(i, headerValue(header)): () => {}"
                  :class="headerAlign(header)"
                  :style="allowEdit && allowDblClickEdit ? `user-select: none; cursor: pointer;` : ``"
                >
                  {{ item[headerValue(header)] != null ? (itemTemplate[headerValue(header)].prefix ? itemTemplate[headerValue(header)].prefix : '') + item[headerValue(header)] + (itemTemplate[headerValue(header)].suffix ? itemTemplate[headerValue(header)].suffix : '') : '-' }}
                </div>

                <!-- Select -->
                <div
                  v-else-if="['select', 'autocomplete', 'combobox'].includes(itemTemplate[headerValue(header)].type)"
                  @dblclick="allowDblClickEdit ? editItem(i, headerValue(header)): () => {}"
                  :class="headerAlign(header)"
                  :style="allowEdit && allowDblClickEdit ? `user-select: none; cursor: pointer;` : ``"
                >
                  {{ item[headerValue(header)] }}
                </div>

                <!-- Boolean -->
                <div
                  v-else-if="['boolean', 'checkbox', 'switch'].includes(itemTemplate[headerValue(header)].type)"
                  @dblclick="allowDblClickEdit ? editItem(i, headerValue(header)): () => {}"
                  :class="headerAlign(header)"
                  :style="allowEdit && allowDblClickEdit ? `user-select: none; cursor: pointer;` : ``"
                >
                  {{ item[headerValue(header)] }}
                </div>

                <!-- Date -->
                <div
                  v-else-if="['date'].includes(itemTemplate[headerValue(header)].type)"
                  @dblclick="allowDblClickEdit ? editItem(i, headerValue(header)): () => {}"
                  :class="headerAlign(header)"
                  :style="allowEdit && allowDblClickEdit ? `user-select: none; cursor: pointer;` : ``"
                >
                  {{ item[headerValue(header)] != null ? convertDateToUTC(new Date(item[headerValue(header)])).toLocaleDateString() : '-' }}
                </div>

                <!-- Time -->
                <div
                  v-else-if="['time'].includes(itemTemplate[headerValue(header)].type)"
                  @dblclick="allowDblClickEdit ? editItem(i, headerValue(header)): () => {}"
                  :class="headerAlign(header)"
                  :style="allowEdit && allowDblClickEdit ? `user-select: none; cursor: pointer;` : ``"
                >
                  {{ item[headerValue(header)] != null ? convertDateFromUTC(new Date(item[headerValue(header)])).toLocaleTimeString() : '-' }}
                </div>

                <!-- DateTime -->
                <div
                  v-else-if="['datetime', 'datetime-local'].includes(itemTemplate[headerValue(header)].type)"
                  @dblclick="allowDblClickEdit ? editItem(i, headerValue(header)): () => {}"
                  :class="headerAlign(header)"
                  :style="allowEdit && allowDblClickEdit ? `user-select: none; cursor: pointer;` : ``"
                >
                  {{ item[headerValue(header)] != null && item[headerValue(header)] !== '' ? convertDateFromUTC(new Date(item[headerValue(header)])).toLocaleString() : '-' }}
                </div>

                <!-- Text Area -->
                <div
                  v-else-if="['textarea'].includes(itemTemplate[headerValue(header)].type)"
                  @dblclick="allowDblClickEdit ? editItem(i, headerValue(header)): () => {}"
                  :class="headerAlign(header)"
                  :style="allowEdit && allowDblClickEdit ? `user-select: none; cursor: pointer;` : ``"
                >
                  {{ item[headerValue(header)] != null ? item[headerValue(header)] : '-' }}
                </div>

                <!-- URL -->
                <div
                  v-else-if="['url'].includes(itemTemplate[headerValue(header)].type)"
                  :class="headerAlign(header)"
                  :style="''"
                >
                  <a :href="itemTemplate[headerValue(header)] ? itemTemplate[headerValue(header)].url.replace('{{' + headerValue(header) + '}}', item[headerValue(header)]) : ''" target="_blank">{{ item[headerValue(header)] != null ? item[headerValue(header)] : '-' }}</a>
                </div>

                <!-- Email -->
                <div
                  v-else-if="['email'].includes(itemTemplate[headerValue(header)].type)"
                  :class="headerAlign(header)"
                  :style="''"
                >
                  <a :href="'mailto:' + item[headerValue(header)]">{{ item[headerValue(header)] != null ? item[headerValue(header)] : '' }}</a>
                </div>

                <!-- List -->
                <div
                  v-else-if="['list'].includes(itemTemplate[headerValue(header)].type)"
                  :class="headerAlign(header)"
                  :style="'white-space: break-spaces;'"
                >
                  {{ item[headerValue(header)] != null ? item[headerValue(header)].map(m => {
                    return Object.values(m).join(' ');
                  }).join('\n') : '-' }}
                </div>

                <!-- Default -->
                <div
                  v-else
                  @dblclick="allowDblClickEdit ? editItem(i, headerValue(header)): () => {}"
                  :class="headerAlign(header)"
                  :style="allowEdit && allowDblClickEdit ? `user-select: none; cursor: pointer;` : ``"
                >
                  {{ item[headerValue(header)] != null && item[headerValue(header)] !== '' ? (itemTemplate[headerValue(header)].prefix ? itemTemplate[headerValue(header)].prefix : '') + item[headerValue(header)] + (itemTemplate[headerValue(header)].suffix ? itemTemplate[headerValue(header)].suffix : '') : '&nbsp;' }}
                </div>
              </div>
            </td>
          </tr>

          <tr
            v-if="lastRowAdd"
          >
            <!-- Create a row of editable text fields, etc... -->
            <td
              v-for="(header, j) in headersLocal"
              :key="'lastrowadd-' + j + '-' + headerValue(header)"
              class="py-1"
              :style="`min-width: ${headerWidth(header)};`"
            >
              <!-- Actions -->
              <div
                v-if="actionsActive && headerValue(header) === '__actions'"
                class="d-flex justify-center align-center"
                style="width: 100%;"
              >
                <slot-chip
                  v-if="lastRowAdd && (permissions & crudPerms) === crudPerms"
                  :disabled="disabled"
                  color="foreground"
                  :size="24"
                  outlined
                  @click="insertItem(lastRowItem)"
                >
                  <v-icon
                    style="font-size: 20px;"
                    color="green"
                  >mdi-plus</v-icon>
                </slot-chip>

                <slot-chip
                  v-if="lastRowAdd"
                  :disabled="disabled"
                  color="foreground"
                  :size="24"
                  outlined
                  @click="resetLastRowAdd()"
                >
                  <v-icon
                    style="font-size: 20px;"
                    color="red"
                  >mdi-cancel</v-icon>
                </slot-chip>
              </div>

              <!-- Disabled -->
              <div
                v-else-if="itemTemplate[headerValue(header)].disabled"
              ></div>

              <!-- Select -->
              <v-select
                v-else-if="itemTemplate[headerValue(header)].type === 'select'"
                v-model="lastRowItem[headerValue(header)]"
                :items="itemTemplate[headerValue(header)].items || []"
                :item-text="itemTemplateItemText(header)"
                :item-value="itemTemplateItemValue(header)"
                outlined
                dense
                hide-details
                class="__data-table-input"
                :rules="[
                  v => !!v || 'Required',
                ]"
                @keydown.enter="insertItem(lastRowItem)"
              />

              <!-- Autocomplete -->
              <v-autocomplete
                v-else-if="itemTemplate[headerValue(header)].type === 'autocomplete'"
                v-model="lastRowItem[headerValue(header)]"
                :items="itemTemplate[headerValue(header)].items || []"
                :item-text="itemTemplateItemText(header)"
                :item-value="itemTemplateItemValue(header)"
                outlined
                dense
                hide-details
                class="__data-table-input"
                :rules="[
                  v => !!v || 'Required',
                ]"
                @keydown.enter="insertItem(lastRowItem)"
              />

              <!-- Autocomplete -->
              <v-combobox
                v-else-if="itemTemplate[headerValue(header)].type === 'combobox'"
                v-model="lastRowItem[headerValue(header)]"
                :items="itemTemplate[headerValue(header)].items || []"
                :item-text="itemTemplateItemText(header)"
                :item-value="itemTemplateItemValue(header)"
                outlined
                dense
                hide-details
                class="__data-table-input"
                :rules="[
                  v => !!v || 'Required',
                ]"
                @keydown.enter="insertItem(lastRowItem)"
              />

              <v-checkbox
                v-else-if="itemTemplate[headerValue(header)].type === 'checkbox'"
                v-model="lastRowItem[headerValue(header)]"
                color="primary"
                dense
                hide-details
                class="ma-0 pa-0 __data-table-checkbox"
                @keydown.enter="insertItem(lastRowItem)"
              />

              <!-- Default -->
              <v-text-field
                v-else
                v-model="lastRowItem[headerValue(header)]"
                :type="itemTemplate[headerValue(header)].type"
                :step="itemTemplate[headerValue(header)].step"
                outlined
                dense
                hide-details
                class="__data-table-input"
                :style="`${itemTemplate[headerValue(header)].hideArrows ? '-moz-appearance: textfield !important;' : ''}`"
                :rules="[
                  v => !!v || 'Required',
                ]"
                @keydown.enter="insertItem(lastRowItem)"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Table pagnation -->
    <table
      class="my-2 ml-auto mr-0"
    >
      <tbody>
        <tr>
          <td>
            <v-icon
              v-if="pageNum > 1"
              style="cursor: pointer; user-select: none;"
              @click="updatePageNum(parseInt(pageNum) - 1)"
            >mdi-arrow-left</v-icon>
            <v-icon
              v-else
              style="cursor: default; user-select: none; opacity: 0;"
            >mdi-arrow-left</v-icon>
          </td>

          <td
            v-if="pageNum < Math.ceil(totalItems / pageSize)"
          >
            <span
              style="user-select: none;"
            >Page</span>
          </td>

          <td
            v-if="pageNum < Math.ceil(totalItems / pageSize)"
          >
            <v-text-field
              v-model="pageNum"
              outlined
              dense
              hide-details
              class="mx-2"
              style="max-width: 50px;"
            ></v-text-field>
          </td>

          <td
            v-if="pageNum < Math.ceil(totalItems / pageSize)"
          >
            <span
              style="user-select: none;"
            ><span class="mr-1">/</span>{{ Math.ceil(totalItems / pageSize) }}</span>
          </td>

          <td>
            <v-icon
              v-if="pageNum < Math.ceil(totalItems / pageSize)"
              style="cursor: pointer; user-select: none;"
              @click="updatePageNum(parseInt(pageNum) + 1)"
            >mdi-arrow-right</v-icon>
            <v-icon
              v-else
              style="cursor: default; user-select: none; opacity: 0;"
            >mdi-arrow-right</v-icon>
          </td>

          <td
            class="px-2"
          ></td>

          <td>
            <span
              style="user-select: none;"
            >Rows:</span>
          </td>

          <td>
            <v-select
              v-model="pageSize"
              :items="pageSizes"
              outlined
              dense
              hide-details
              class="ml-2"
              style="max-width: 100px;"
            ></v-select>
          </td>
        </tr>
      </tbody>
    </table>

    <!-- Add Item -->
    <custom-dialog
      :dialog="dialogAdd"
      title="Add Row"
      :headers="headersLocal"
      :itemTemplate="itemTemplate"
      type="add"
      @dialog="dialogAdd = $event;"
      @submit="insertItem($event)"
    ></custom-dialog>

    <!-- Edit Item -->
    <custom-dialog
      :dialog="dialogEdit"
      title="Edit Row"
      :headers="headersLocal"
      :itemTemplate="itemTemplate"
      :item="editedItem"
      type="edit"
      @dialog="dialogEdit = $event;"
      @submit="updateItem(editedItemIndex, $event)"
    ></custom-dialog>

    <!-- Confirm Delete -->
    <confirm-dialog
      :dialog="dialogDelete"
      title="Delete Row"
      :message="`Are you sure you want to delete this entry?`"
      :data="listItem(itemDelete)"
      :passthrough="itemDelete"
      @dialog="dialogDelete = $event;"
      @confirm="deleteItem($event);"
      @cancel="dialogDelete = false;"
    ></confirm-dialog>
  </div>
</template>
<script>
import CustomDialog from './CustomDialog.vue';
import ConfirmDialog from './ConfirmDialog.vue';
import SlotChip from './SlotChip.vue';

export default {
  name: '',

  components: {
    CustomDialog,
    ConfirmDialog,
    SlotChip,
  },

  props: {
    permissions: {
      type: Number,
      required: true,
    },

    crudPerms: {
      type: Number,
      required: true,
    },

    title: {
      type: String,
      required: false,
      default: '',
    },

    headers: {
      type: Array,
      required: true,
    },

    itemTemplate: {
      type: Object,
      required: true,
      default: {},
    },

    customActions: {
      type: Array,
      required: false,
      default: () => [],
    },

    items: {
      type: Array,
      required: true,
    },

    totalItems: {
      type: Number,
      required: false,
      default: 0,
    },

    loading: {
      type: Boolean,
      required: false,
      default: false,
    },

    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },

    allowDownload: {
      type: Boolean,
      required: false,
      default: false,
    },

    allowRefresh: {
      type: Boolean,
      required: false,
      default: true,
    },

    allowAdd: {
      type: Boolean,
      required: false,
      default: false,
    },

    lastRowAdd: {
      type: Boolean,
      required: false,
      default: false,
    },

    allowEdit: {
      type: Boolean,
      required: false,
      default: false,
    },

    allowDelete: {
      type: Boolean,
      required: false,
      default: false,
    },

    allowSearch: {
      type: Boolean,
      required: false,
      default: true,
    },

    allowSort: {
      type: Boolean,
      required: false,
      default: true,
    },

    allowDblClickEdit: {
      type: Boolean,
      required: false,
      default: true,
    },

    customButtons: {
      type: Array,
      required: false,
      default: () => [],
    },

    revert: {
      type: Boolean,
      required: true,
      default: false,
    }
  },

  data: () => ({
    tableKey: 0,
    dialogKey: 0,
    actionsActive: false,
    headersLocal: [],
    showHeaders: false,
    itemsCopy: [], // Copy of items and transformed to table values
    itemsSorted: [], // Sorted of itemsCopy
    itemsFiltered: [], // Filtered of itemsSorted
    itemsShow: [], // Showed of itemsFiltered
    lastRowItem: {},
    editedItem: {},
    editedItemIndex: -1,
    dialogAdd: false,
    dialogEdit: false,
    dialogDelete: false,
    itemDelete: null,
    search: '',
    sortBy: '',
    sortDir: '',
    pageNum: 1,
    tmppageNum: 1,
    pageSizes: [
      { text: '25', value: 25 },
      { text: '50', value: 50 },
      { text: '100', value: 100 },
      { text: 'All', value: null },
    ],
    pageSize: 25,
    USDollar: null,
    internalLoading: false,
  }),

  watch: {
    revert: {
      handler() {
        this.loadItems();
        this.$emit('reverted');
      },
      immediate: true,
    },

    loading: {
      handler() {
        if (!this.loading) {
          this.tableKey++;
        }
      },
      immediate: true,
    },

    headers: {
      handler() {
        this.createHeaders();
      },
      deep: true,
    },

    items: {
      handler() {
        this.loadItems();
      },
      deep: true,
    },

    itemsShow: {
      handler() {
        this.resetItemsEdit();
      },
      deep: true,
    },

    pageSize() {
      this.itemsShow = this.itemsFiltered.slice((this.pageNum - 1) * this.pageSize, this.pageNum * this.pageSize);
      // keep pageSize in sync with pageSize so it can be included in the URL
      this.pageSize = this.pageSize;
      this.updateQuery();
    },
  },

  computed: {
    primaryKeys() {
      return this.headersLocal.filter(f => f.primaryKey).map(f => f.value);
    },
  },

  watch: {

  },

  methods: {
    updatePageNum(newPageNum) {
      console.log('pageNum changed to', newPageNum);
      if (newPageNum < 1) {
        this.pageNum = 1;
      }
      else if (newPageNum > Math.ceil(this.totalItems / this.pageSize)) {
        this.pageNum = Math.ceil(this.totalItems / this.pageSize);
      }
      else {
        this.pageNum = newPageNum;
      }
      this.itemsShow = this.itemsFiltered.slice((this.pageNum - 1) * this.pageSize, this.pageNum * this.pageSize);
      this.updateQuery();
    },

    download() {
      if (this.allowDownload) {
        const csv = this.headersLocal.filter(f => f.show && f.value !== '__actions').map(header => header.text.includes(',') ? `"${header.text}"` : header.text).join(',') + '\n' +
          this.itemsFiltered.map(row => {
            return this.headersLocal.filter(f => f.show && f.value !== '__actions').map(header => {
              return row[this.headerValue(header)] != null && typeof row[this.headerValue(header)] === 'string' ? (row[this.headerValue(header)].includes(',') ? `"${row[this.headerValue(header)]}"` : row[this.headerValue(header)]) :
              Array.isArray(row[this.headerValue(header)]) ? `"${row[this.headerValue(header)].map(m => {
                return m != null && typeof m === 'string' ? m : m != null && typeof m === 'object' ? Object.values(m).join(' ') : m;
              }).join(' | ')}"` :
              row[this.headerValue(header)] != null && typeof row[this.headerValue(header)] === 'object' ? (Object.values(row[this.headerValue(header)]).join(' ').includes(',') ? `"${Object.values(row[this.headerValue(header)]).join(' ')}"` : Object.values(row[this.headerValue(header)]).join(' ')) :
              row[this.headerValue(header)];
            }).join(',');
          }).join('\n');

        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${this.title}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      }
    },

    copy(items) {
      const tsv = this.headersLocal.filter(f => f.show && f.value !== '__actions').map(header => header.text).join('\t') + '\n' +
      this.itemsCopy.map(row => {
        return this.headersLocal.filter(f => f.show && f.value !== '__actions').map(header => {
          return row[this.headerValue(header)] != null && typeof row[this.headerValue(header)] === 'string' ? (row[this.headerValue(header)].includes('\t') ? `"${row[this.headerValue(header)]}"` : row[this.headerValue(header)]) :
          Array.isArray(row[this.headerValue(header)]) ? `"${row[this.headerValue(header)].map(m => {
            return m != null && typeof m === 'string' ? m : m != null && typeof m === 'object' ? Object.values(m).join(' ') : m;
          }).join(' | ')}"` :
          row[this.headerValue(header)] != null && typeof row[this.headerValue(header)] === 'object' ? (Object.values(row[this.headerValue(header)]).join(' ').includes('\t') ? `"${Object.values(row[this.headerValue(header)]).join(' ')}"` : Object.values(row[this.headerValue(header)]).join(' ')) :
          row[this.headerValue(header)];
        }).join('\t');
      }).join('\n');

      navigator.clipboard.writeText(tsv);
    },

    updateShowHeader(header, value) {
      if (!value) {
        this.sortBy = this.sortBy.filter(f => f.sortBy !== this.headerValue(header));
        this.sortTable();
      }
      this.tableKey++;
    },

    setSort(header) {
      const idx = this.sortBy.findIndex(f => f.sortBy === this.headerValue(header));

      if (idx === -1) {
        this.sortBy.unshift({ sortBy: this.headerValue(header), sortDir: 'asc' });
      }
      else {
        if (this.sortBy[idx].sortDir === 'asc') {
          this.sortBy[idx].sortDir = 'desc';
        }
        else {
          this.sortBy.splice(idx, 1);
        }
      }

      this.sortTable();
    },

    defaultSort() {
      return this.headersLocal.filter(f => f.sortBy).sort((a, b) => b.sortBy - a.sortBy).map(f => {
        return {
          sortBy: f.value,
          sortDir: f.sortDir ? f.sortDir : 'asc',
        }
      })
    },

    isHidden(header) {
      const template = this.itemTemplate[this.headerValue(header)];
      return template && template.hidden ? (typeof template.hidden === 'object' ? template.hidden.add === true && template.hidden.edit === true : template.hidden) : false;
    },

    isHiddenEdit(header) {
      const template = this.itemTemplate[this.headerValue(header)];
      return template && typeof template.hidden === 'object' ? template.hidden.edit : template.hidden;
    },

    isHiddenAdd(header) {
      const template = this.itemTemplate[this.headerValue(header)];
      return template && typeof template.hidden === 'object' ? template.hidden.add : template.hidden;
    },

    isDisabled(header) {
      const template = this.itemTemplate[this.headerValue(header)];
      return template && template.disabled ? (typeof template.disabled === 'object' ? template.disabled.add && template.disabled.edit : template.disabled) : false;
    },

    isDisabledEdit(header) {
      const template = this.itemTemplate[this.headerValue(header)];
      return template && typeof template.disabled === 'object' ? template.disabled.edit : template.disabled;
    },

    isDisabledAdd(header) {
      const template = this.itemTemplate[this.headerValue(header)];
      return template && typeof template.disabled === 'object' ? template.disabled.add : template.disabled;
    },

    listItem(item) {
      if (item) {
        return (item ? `${this.headersLocal.filter(f => !this.isHidden(f) && f.value !== '__actions').map(m => m.value).map(k => `${this.itemTemplate[k].label}: ${item[k]}`).join('\n')}` : '');
      }
      return '';
    },

    convertDateToUTC(date) {
      const offset = new Date().getTimezoneOffset();
      return new Date(date.getTime() + offset * 60 * 1000);
    },

    convertDateFromUTC(date) {
      const offset = new Date().getTimezoneOffset();
      return new Date(date.getTime() - offset * 60 * 1000);
    },

    validDate(date) {
      const match = date.match(/(\d{4})-(\d{2})-(\d{2})/);
      if (match) {
        const year = parseInt(match[1], 10);
        const month = parseInt(match[2], 10) - 1; // Months are 0-indexed in JavaScript
        const day = parseInt(match[3], 10);
        const d = new Date(year, month, day);
        const ret = d.getFullYear() === year && d.getMonth() === month && d.getDate() === day;
        if (!ret) {
          console.warn('Invalid date:', date, '. Expected format: YYYY-MM-DD');
        }
        return ret;
      }

      console.warn('Invalid date format:', date, '. Expected format: YYYY-MM-DD');
      return false;
    },

    validTime(time) {
      const match = time.match(/(\d{2}):(\d{2})(:(\d{2}))?/);
      if (match) {
        const hours = parseInt(match[1], 10);
        const minutes = parseInt(match[2], 10);
        const seconds = match[4] ? parseInt(match[4], 10) : 0;
        const ret = hours >= 0 && hours < 24 && minutes >= 0 && minutes < 60 && seconds >= 0 && seconds < 60;
        if (!ret) {
          console.warn('Invalid time:', time, '. Expected format: HH:MM[:SS]');
        }
        return ret;
      }

      console.warn('Invalid time format:', time, '. Expected format: HH:MM[:SS]');
      return false;
    },

    transformToTable(item) {
      if (!item) return null;

      const newItem = {};
      for (const key of Object.keys(this.itemTemplate)) {
        if (['boolean', 'checkbox', 'switch'].includes(this.itemTemplate[key].type)) {
          newItem[key] = this.itemTemplate[key].text ? (!item[key] ? this.itemTemplate[key].text.false : this.itemTemplate[key].text.true) : (!item[key] ? 0 : 1)
        }
        else if (['number', 'currency'].includes(this.itemTemplate[key].type)) {
          newItem[key] = item[key] != null ? Number(item[key]) : null;
        }
        else if (['select', 'autocomplete', 'combobox'].includes(this.itemTemplate[key].type)) {
          if (this.itemTemplate[key].multiple && item[key] && Array.isArray(item[key])) {
            let names = [];
            for (const m of item[key]) {
              let val = this.itemSelectText(key, { permissions: m });
              if (val && val.length) {
                names.push(val);
              }
            }
            newItem[key] = names.join(', ');
          }
          else {
            newItem[key] = this.itemSelectText(key, item);
          }
        }
        else if (['date'].includes(this.itemTemplate[key].type)) {
          newItem[key] = item[key] && new Date(item[key]) !== 'Invalid Date' && this.validDate(item[key]) ? new Date(item[key]).toISOString().split('T')[0] : null;
        }
        else if (item[key] && ['time'].includes(this.itemTemplate[key].type)) {
          newItem[key] = item[key] && new Date(item[key]) !== 'Invalid Date' && this.validTime(item[key]) ? new Date(item[key]).toISOString().substring(0, 5) : null;
        }
        else if (item[key] && ['datetime', 'datetime-local'].includes(this.itemTemplate[key].type)) {
          newItem[key] = item[key] && new Date(item[key]) !== 'Invalid Date' && this.validDate(item[key]) && this.validTime(item[key]) ? new Date(item[key]).toISOString() : null;
        }
        else {
          newItem[key] = item[key];
        }
      }

      return newItem;
    },

    transformFromTable(item) {
      if (!item) return null;

      const newItem = {};
      for (const key of Object.keys(this.itemTemplate)) {
        if (['boolean', 'checkbox', 'switch'].includes(this.itemTemplate[key].type)) {
          newItem[key] = this.itemTemplate[key].text ? (item[key] === this.itemTemplate[key].text.true ? 1 : 0) : (!item[key] ? 0 : 1);
        }
        else if (['number', 'currency'].includes(this.itemTemplate[key].type)) {
          newItem[key] = item[key] !== '' && item[key] != null ? Number(item[key]) : null;
        }
        else if (['date'].includes(this.itemTemplate[key].type)) {
          newItem[key] = item[key] && new Date(item[key]) !== 'Invalid Date' ? new Date(item[key]).toISOString().split('T')[0] : null;
        }
        else if (['time'].includes(this.itemTemplate[key].type)) {
          newItem[key] = item[key] && new Date(item[key]) !== 'Invalid Date' ? new Date(item[key]).toISOString().split('T')[1].substring(0, 5) : null;
        }
        else if (['datetime', 'datetime-local'].includes(this.itemTemplate[key].type)) {
          newItem[key] = item[key] && new Date(item[key]) !== 'Invalid Date' ? new Date(item[key]).toISOString().replace('T', ' ').substring(0, 19) : null;
        }
        else if (['select', 'autocomplete', 'combobox'].includes(this.itemTemplate[key].type)) {
          if (this.itemTemplate[key].multiple && item[key] && !Array.isArray(item[key])) {
            const names = item[key].split(', ');
            const values = [];
            for (const m of names) {
              values.push(this.itemSelectValueFromText(key, m))
            }
            newItem[key] = values;
          }
          else {
            newItem[key] = item[key] ? this.itemSelectValueFromText(key, item[key]) : '';
          }
        }
        else {
          newItem[key] = item[key];
        }
      }

      return newItem;
    },

    loadItems() {
      this.itemsCopy = JSON.parse(JSON.stringify(this.items));
      this.itemsCopy = this.preTransformItems(this.itemsCopy);

      if (this.itemsCopy.length < 250) {
        this.pageSizes[this.pageSizes.length - 1].text = 'All';
        this.pageSizes[this.pageSizes.length - 1].value = this.itemsCopy.length;
      }
      else {
        this.pageSizes[this.pageSizes.length - 1].text = '250';
        this.pageSizes[this.pageSizes.length - 1].value = 250;
      }

      this.resetLastRowAdd();
      for (const i in this.itemsCopy) {
        this.itemsCopy[i] = this.transformToTable(this.itemsCopy[i]);
      }
      this.itemsSorted = JSON.parse(JSON.stringify(this.itemsCopy));
      this.sortTable();
    },

    resetLastRowAdd() {
      for (const k of Object.keys(this.itemTemplate)) {
        if (['boolean', 'checkbox', 'switch'].includes(this.itemTemplate[k].type)) {
          this.lastRowItem[k] = this.itemTemplate[k].default ? this.itemTemplate[k].default : false;
        }
        else if (['select', 'autocomplete', 'combobox'].includes(this.itemTemplate[k].type)) {
          this.lastRowItem[k] = this.itemTemplate[k].default ? this.itemTemplate[k].default : this.itemTemplate[k].items && this.itemTemplate[k].items.length > 0 && this.itemTemplate[k].items[0][this.itemTemplate[k]['item-value'] ? this.itemTemplate[k]['item-value'] : 'value'];
        }
        else if (this.itemTemplate[k].type === 'number') {
          this.lastRowItem[k] = this.itemTemplate[k].default ? this.itemTemplate[k].default : 0;
        }
        else {
          this.lastRowItem[k] = this.itemTemplate[k].default ? this.itemTemplate[k].default : '';
        }
      }
    },

    updateQuery() {
      const q = {};
      // Get initial query
      const urlParams = new URLSearchParams(window.location.search);

      if (this.sortBy && this.sortBy.length > 0) {
        q.sortBy = this.sortBy.map(m => m.sortBy).join(',');
        q.sortDir = this.sortBy.map(m => m.sortDir).join(',');
      }
      if (this.pageNum) {
        q.pageNum = this.pageNum;
      }
      // include pageSize in the query so backends/links can read the current page size
      if (this.pageSize) {
        q.pageSize = this.pageSize;
      }
      if (this.search && this.search !== '') {
        q.search = this.search;
      }

      history.pushState({}, '', `${location.pathname}${Object.keys(q).length > 0 ? '?' : ''}${Object.keys(q).map(m => `${m}=${q[m]}`).join('&')}`);


      // Check if query changed
      if (urlParams.get('sortBy') !== q.sortBy ||
          urlParams.get('sortDir') !== q.sortDir ||
          (urlParams.get('pageNum') ? parseInt(urlParams.get('pageNum'), 10) : 1) !== (q.pageNum ? q.pageNum : 1) ||
          (urlParams.get('pageSize') ? (urlParams.get('pageSize') === 'null' ? null : parseInt(urlParams.get('pageSize'), 10)) : null) !== (q.pageSize ? q.pageSize : null) ||
          urlParams.get('search') !== (q.search ? q.search : '')) {
        this.$emit('query-updated', q);
      }
    },

    headerValue(header) {
      return typeof header === 'object' && header.value ? header.value : header;
    },

    headerText(header) {
      return typeof header === 'object' && header.text ? header.text : header;
    },

    headerWidth(header) {
      return typeof header === 'object' && header.width ? header.width : 'auto';
    },

    headerAlign(header) {
      const align = typeof header === 'object' && header.align ? header.align : 'center';
      return `text-${align}`;
    },

    headerItemCompare(header, a, b) {
      return this.itemTemplate[this.headerValue(header)].compare ? this.itemTemplate[this.headerValue(header)].compare(a, b) : a === b;
    },

    itemTemplateItemText(header) {
      return this.itemTemplate[this.headerValue(header)]['item-text'] ? this.itemTemplate[this.headerValue(header)]['item-text'] : 'text';
    },

    itemTemplateItemValue(header) {
      return this.itemTemplate[this.headerValue(header)]['item-value'] ? this.itemTemplate[this.headerValue(header)]['item-value'] : 'value';
    },

    itemSelectText(header, item) {
      return item[this.headerValue(header)] != null && Array.isArray(this.itemTemplate[this.headerValue(header)].items) ? this.itemTemplate[this.headerValue(header)].items.filter(f => this.headerItemCompare(header, item[this.headerValue(header)], f[this.itemTemplateItemValue(header)])).map(f => f[this.itemTemplateItemText(header)]).join(', ') : '-'
    },

    itemSelectValueFromText(header, text) {
      const item = this.itemTemplate[this.headerValue(header)].items.find(f => f[this.itemTemplateItemText(this.headerValue(header))] === text);
      return item ? item[this.itemTemplateItemValue(header)] : text;
    },

    insertItemDialog() {
      this.dialogAdd = true;
    },

    insertItem(item) {
      this.dialogAdd = false;
      delete item['__data-table-edit'];

      // Save current query/state so parent can persist it before reload
      try {
        this.updateQuery();
      }
      catch (e) {
        console.warn('Failed to update query before insert:', e);
      }

      const newItem = this.transformToTable(item);
      this.itemsCopy.push(newItem);
      this.sortTable();
      this.$emit('add-item', item);
    },

    updateItemDialog(event, item) {
      this.resetItemsEdit();
      this.editedItemIndex = this.itemsCopy.findIndex(f => this.primaryKeys.every(k => f[k] === item[k]));
      this.editedItem = JSON.parse(JSON.stringify(this.transformFromTable(item)));
      this.dialogEdit = true;
    },

    updateItem(i, item) {
      this.dialogEdit = false;

      // Save current query/state so parent can persist it before reload
      try {
        this.updateQuery();
      }
      catch (e) {
        console.warn('Failed to update query before update:', e);
      }

      console.log('Updating item at index:', i, 'with item:', item);
      const oldItem = JSON.parse(JSON.stringify(this.postTransformItem(this.itemsCopy[i])));
      const newItem = this.transformToTable(item);
      for (const key of Object.keys(item)) {
        if (!this.itemTemplate[key].disabled && oldItem[key] !== item[key] && this.itemsCopy[i][key] !== newItem[key]) {
          console.log('Updating key:', key, 'from', oldItem[key], 'to', item[key]);
          console.log('Updating key:', key, 'from', this.itemsCopy[i][key], 'to', newItem[key]);
          this.itemsCopy[i][key] = newItem[key];
        }
      }
      this.sortTable();
      this.$emit('update-item', { old: this.postTransformItem(this.items[i]), new: this.postTransformItem(item) });
    },

    deleteItem(item) {
      this.dialogDelete = false;
      delete item['__data-table-edit'];

      // Save current query/state so parent can persist it before reload
      try {
        this.updateQuery();
      }
      catch (e) {
        console.warn('Failed to update query before delete:', e);
      }

      this.itemsCopy.splice(this.itemsCopy.findIndex(f => this.primaryKeys.every(k => f[k] === item[k])), 1);
      this.sortTable();
      this.$emit('delete-item', item);
    },

    resetItemsEdit() {
      for (let i = 0; i < this.itemsCopy.length; i++) {
        if (this.itemsCopy[i]['__data-table-edit']) {
          this.itemsCopy[i]['__data-table-edit'] = false;
        }
      }
    },

    editItem(i, header) {
      if (this.allowEdit && !this.isDisabledEdit(header)) {
        if (typeof this.itemTemplate[header].disabled === 'object' ? this.itemTemplate[header].disabled.edit : this.itemTemplate[header].disabled) return;

        if (['boolean', 'checkbox', 'switch'].includes(this.itemTemplate[this.headerValue(header)].type)) {
          this.itemsEdit[i][header] = this.itemsEdit[i][header] === 1 || this.itemsEdit[i][header] === this.itemTemplate[header].text.true ? false : true;
          // Save current query/state so parent can persist it before reload
          try {
            this.updateQuery();
          }
          catch (e) {
            console.warn('Failed to update query before inline boolean update:', e);
          }

          this.$emit('update-item', { old: this.postTransformItem(this.itemsShow[i]), new: this.postTransformItem(this.itemsEdit[i]) });

          // Local update for immediate UI change
          const itemCopyIdx = this.itemsCopy.findIndex(f => f === this.itemsShow[i]);
          this.itemsCopy[itemCopyIdx] = JSON.parse(JSON.stringify(this.itemsEdit[i]));
          this.sortTable();
        }
        else {
          this.itemsShow[i]['__data-table-edit'] = this.allowEdit ? header : false;
          this.$forceUpdate();
          this.$nextTick(() => {
            if (['select', 'autocomplete', 'combobox'].includes(this.itemTemplate[this.headerValue(header)].type)) {
              document.getElementById('edit-' + i + '-' + header).select();
            }
            else {
              document.getElementById('edit-' + i + '-' + header).focus();
            }
          });
        }
      }
    },

    preTransformItems(items) {
      for (let i = 0; i < items.length; i++) {
        for (const key of Object.keys(this.itemTemplate)) {
          if (this.itemTemplate[key].preTransform != null) {
            items[i][key] = this.itemTemplate[key].preTransform(items[i][key]);
          }
        }
      }
      return items;
    },

    preTransformItem(item) {
      const itemCopy = {};
      for (const key of Object.keys(this.itemTemplate)) {
        if (this.itemTemplate[key].preTransform != null) {
          itemCopy[key] = this.itemTemplate[key].preTransform(item[key]);
        }
        else {
          itemCopy[key] = item[key];
        }
      }
      return itemCopy;
    },

    postTransformItem(item) {
      const itemCopy = {};
      for (const key of Object.keys(this.itemTemplate)) {
        if (this.itemTemplate[key].postTransform != null) {
          itemCopy[key] = this.itemTemplate[key].postTransform(item[key]);
        }
        else {
          itemCopy[key] = this.itemTemplate[key].items && Array.isArray(this.itemTemplate[key].items) && this.itemTemplate[key].items.find(f => f[this.itemTemplateItemText(key)] === item[key]) ? this.itemTemplate[key].items.find(f => f[this.itemTemplateItemText(key)] === item[key])[this.itemTemplateItemValue(key)] : item[key];
        }
      }
      return itemCopy;
    },

    searchKey(e) {
      if (e.key === 'Enter') {
        this.internalLoading = true;
        this.$nextTick(() => {
          this.searchTable(e);
          this.internalLoading = false;
        })
      }
    },

    searchTable(e) {
      if (this.allowSearch) {
        const search = this.search.toLowerCase().trim();

        if (search.length > 0) {
          this.itemsFiltered = this.itemsSorted.filter(item => {
            for (const header of this.headersLocal) {
              if (this.headerValue(header) === '__actions') continue;

              if (this.itemTemplate[this.headerValue(header)].type === 'list' && item[this.headerValue(header)].length > 0) {
                if (typeof item[this.headerValue(header)][0] === 'object') {
                  for (const listItem of item[this.headerValue(header)]) {
                    for (const key of Object.keys(listItem)) {
                      if (listItem[key] && listItem[key].toString().toLowerCase().includes(search)) {
                        return true;
                      }
                    }
                  }
                }
                else {
                  if (item[this.headerValue(header)].join(' ').toLowerCase().includes(search)) {
                    return true;
                  }
                }
              }
              else if (item[this.headerValue(header)] && item[this.headerValue(header)].toString().toLowerCase().includes(search)) {
                return true;
              }
            }
            return false;
          });
        }
        else {
          this.itemsFiltered = JSON.parse(JSON.stringify(this.itemsSorted));
        }

        // Change table page to 1 when searching, hold current table in temp variable
        if (search.length > 0 && this.tmppageNum === -1  && e && e.key && e.key !== 'Backspace') {
          this.tmppageNum = this.pageNum;
          this.pageNum = 1;
        }
        // Revert back to table page when search was empty
        else if (search.length === 0 && e && e.key && e.key === 'Backspace') {
          this.pageNum = this.tmppageNum;
          this.tmppageNum = -1;
        }

        this.itemsShow = this.itemsFiltered.slice((this.pageNum - 1) * this.pageSize, this.pageNum * this.pageSize);
        this.updateQuery();
      }
    },

    sortTable() {
      if (this.allowSort) {
        for (let i = 0; i < this.itemsShow.length; i++) {
          this.itemsShow[i]['__data-table-edit'] = false;
        }

        this.itemsSorted = JSON.parse(JSON.stringify(this.itemsCopy));
        for (const sort of this.sortBy) {
          this.itemsSorted.sort((a, b) => {
            if (sort.sortDir === 'asc') {
              if (['select', 'autocomplete', 'combobox'].includes(this.itemTemplate[sort.sortBy].type)) {
                if (typeof a[sort.sortBy] === 'string' && typeof b[sort.sortBy] === 'string') {
                  return a[sort.sortBy].localeCompare(b[sort.sortBy]);
                }
                else if (typeof a[sort.sortBy] === 'string' && !typeof b[sort.sortBy] === 'string') {
                  return -1;
                }
                else if (!typeof a[sort.sortBy] === 'string' && typeof b[sort.sortBy] === 'string') {
                  return 1;
                }
                return a[sort.sortBy] > b[sort.sortBy] ? 1 : a[sort.sortBy] < b[sort.sortBy] ? -1 : 0;
              }
              else if (['date', 'time', 'datetime', 'datetime-local'].includes(this.itemTemplate[sort.sortBy].type)) {
                if (a[sort.sortBy] && b[sort.sortBy]) {
                  return a[sort.sortBy].toLowerCase().localeCompare(b[sort.sortBy].toLowerCase());
                }
                else if (a[sort.sortBy] && !b[sort.sortBy]) {
                  return -1;
                }
                else if (!a[sort.sortBy] && b[sort.sortBy]) {
                  return 1;
                }
              }
              else if (['list'].includes(this.itemTemplate[sort.sortBy].type)){
                return a[sort.sortBy].map(m => {
                    return Object.values(m).join(' ');
                }).join(' ').toLowerCase().localeCompare(
                  b[sort.sortBy].map(m => {
                    return Object.values(m).join(' ');
                  }).join(' ').toLowerCase()
                )
              }
              else if (typeof a[sort.sortBy] === 'string' && typeof b[sort.sortBy] === 'string') {
                return a[sort.sortBy].toLowerCase().localeCompare(b[sort.sortBy].toLowerCase());
              }
              else if (typeof a[sort.sortBy] === 'string' && typeof b[sort.sortBy] !== 'string') {
                return -1;
              }
              else if (typeof a[sort.sortBy] !== 'string' && typeof b[sort.sortBy] === 'string') {
                return 1;
              }
              return parseFloat(a[sort.sortBy]) > parseFloat(b[sort.sortBy]) ? 1 : parseFloat(a[sort.sortBy]) < parseFloat(b[sort.sortBy]) ? -1 : 0;
            }
            else if (sort.sortDir === 'desc') {
              if (['select', 'autocomplete', 'combobox'].includes(this.itemTemplate[sort.sortBy].type)) {
                if (typeof a[sort.sortBy] === 'string' && typeof b[sort.sortBy] === 'string') {
                  return b[sort.sortBy].localeCompare(a[sort.sortBy]);
                }
                else if (typeof a[sort.sortBy] === 'string' && !typeof b[sort.sortBy] === 'string') {
                  return 1;
                }
                else if (!typeof a[sort.sortBy] === 'string' && typeof b[sort.sortBy] === 'string') {
                  return -1;
                }
                return a[sort.sortBy] > b[sort.sortBy] ? -1 : a[sort.sortBy] < b[sort.sortBy] ? 1 : 0;
              }
              else if (['date', 'time', 'datetime', 'datetime-local'].includes(this.itemTemplate[sort.sortBy].type)) {
                if (a[sort.sortBy] && b[sort.sortBy]) {
                  return b[sort.sortBy].localeCompare(a[sort.sortBy]);
                }
                else if (a[sort.sortBy] && !b[sort.sortBy]) {
                  return -1;
                }
                else if (!a[sort.sortBy] && b[sort.sortBy]) {
                  return 1;
                }
              }
              else if (['list'].includes(this.itemTemplate[sort.sortBy].type)){
                return b[sort.sortBy].map(m => {
                    return Object.values(m).join(' ');
                }).join(' ').toLowerCase().localeCompare(
                  a[sort.sortBy].map(m => {
                    return Object.values(m).join(' ');
                  }).join(' ').toLowerCase()
                )
              }
              else if (typeof a[sort.sortBy] === 'string' && typeof b[sort.sortBy] === 'string') {
                return b[sort.sortBy].localeCompare(a[sort.sortBy]);
              }
              else if (typeof a[sort.sortBy] === 'string' && typeof b[sort.sortBy] !== 'string') {
                return 1;
              }
              else if (typeof a[sort.sortBy] !== 'string' && typeof b[sort.sortBy] === 'string') {
                return -1;
              }
              return parseFloat(a[sort.sortBy]) < parseFloat(b[sort.sortBy]) ? 1 : parseFloat(a[sort.sortBy]) > parseFloat(b[sort.sortBy]) ? -1 : 0;
            }
            return 0;
          })
        }

        this.searchTable();
      }
    },

    keydown(e) {
      if (e.key === 'Escape') {
        this.resetItemsEdit();
        this.$forceUpdate();
      }
    },

    setInitialQuery() {
      this.search = this.$route.query.search || '';

      const sortBys = this.$route.query.sortBy ? this.$route.query.sortBy.split(',') : [];
      const sortDirs = this.$route.query.sortDir ? this.$route.query.sortDir.split(',') : [];

      if (sortBys.length !== 0 && sortBys.length === sortDirs.length) {
        this.sortBy = sortBys.map((f, i) => {
          return {
            sortBy: f,
            sortDir: sortDirs[i],
          }
        })
      }
      else {
        this.sortBy = this.defaultSort();
      }

      this.pageNum = parseInt(this.$route.query.pageNum) || 1;
      // support legacy `pageSize` query param but prefer `pageSize` if present
      this.pageSize = parseInt(this.$route.query.pageSize) || this.pageSizes[0].value;

      this.updateQuery();
    },

    createHeaders() {
      this.headersLocal = JSON.parse(JSON.stringify(this.headers));

      // Populate header info into item template
      for (const header of this.headersLocal) {
        for (const key of Object.keys(this.itemTemplate)) {
          if (this.headerValue(header) === key) {
            this.itemTemplate[key].label = header.text;
          }
          if (this.isHiddenEdit(header)) {
            header.show = false;
          }
          else {
            header.show = true;
          }
        }
      }

      // Check if actions are active
      if (this.allowEdit || this.allowDelete || this.lastRowAdd) {
        let width = 0;
        // Table rows
        if (this.allowDelete) {
          width += 32;
        }
        if (this.allowRefresh) {
          width += 32;
        }
        if (this.allowEdit) {
          width += 32;
        }

        // Last row add
        if (this.lastRowAdd) {
          width = 64;
        }

        if (!this.headersLocal.find(f => f.value === '__actions')) {
          this.headersLocal.push({ text: 'Actions', value: '__actions', width: width + 'px', centered: true, show: true });
        }

        this.actionsActive = true;
      }
      else {
        this.actionsActive = false;
      }

      this.dialogKey++;
    }
  },

  destroyed() {
    window.removeEventListener('keydown', this.keydown, true);
  },

  created() {
    window.addEventListener('keydown', this.keydown, true);

    this.USDollar = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    });

    this.createHeaders();
    this.setInitialQuery();
  },

  mounted() {
    this.loadItems();
  }
}
</script>
<style scoped>
  /* TABLE */
  .__data-table {
    border-collapse: collapse;
    border-spacing: 0;
    width: 100%;
    background: var(--v-tableBackground-base);
    color: var(--v-foreground-base);
  }

  /* THEAD */
  .__data-table > thead {
    font-size: 12px;
    text-align: left;
    border-bottom: 1px solid var(--v-gray-base);
  }
  .__data-table > .header-active-light {
    color: #000000DE !important;
  }
  .__data-table > .header-active-dark {
    color: #FFFFFF !important;
  }

  /* TH */
  .__data-table > thead > tr > th {
    height: 32px;
    padding: 0 8px !important;
    white-space: nowrap;
    overflow: auto;
    /* border-right: 1px solid var(--v-foreground-base); */
  }
  .__data-table > thead > tr > th:hover {
    background: var(--v-tableRowHover-base);
  }

  /* TBODY */
  .__data-table > tbody {
    font-size: 14px;
    /* border-bottom: 1px solid var(--v-foreground-base); */
    /* border: 1px solid var(--v-foreground-base); */
  }

  /* TR */
  .__data-table > tbody > tr:nth-child(even) {
    background: var(--v-tableBackground-base);
  }
  /* odd row styling intentionally left to inherit or use alternate backgrounds via theme variables */
  .__data-table > tbody > tr:hover {
    background: var(--v-tableRowHover-base);
  }

  /* TD */
  .__data-table > tbody > tr > td {
    height: 32px;
    padding: 4px 8px !important;
    overflow: auto;
    /* border-right: 1px dashed var(--v-foreground-base); */
    border-bottom: 1px solid var(--v-gray-base);
  }

  /* INPUT */
  .__data-table-input > .v-input__control > .v-input__slot > .v-select__slot > input,
  .__data-table-input > .v-input__control > .v-input__slot > .v-text-field__slot > input {
    padding: 5px 5px !important;
    min-height: 0 !important;
    min-width: 60px;
    font-size: 14px !important;
    background: var(--v-tableBackground-base);
  }

  .__data-table-input-hide-arrows > .v-input__control > .v-input__slot > .v-text-field__slot > input {
    appearance: textfield;
    -moz-appearance: textfield;
  }

  .__data-table-input > .v-input__control > .v-input__slot {
    min-height: 34px !important;
  }

  .__data-table-input > .v-input__control > .v-input__slot > .v-select__slot > .v-input__append-inner {
    margin-top: 4px !important;
  }

  .__data-table-input > .v-select.v-text-field--outlined:not(.v-text-field--single-line).v-input--dense .v-select__selections {
    padding: 0 !important;
  }

  .__data-table-checkbox > .v-input__control, .__data-table-checkbox > .v-input__control > .v-input__slot {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px !important;
    height: 24px !important;
  }

  .__data-table-checkbox > .v-input__control > .v-input__slot > .v-input--selection-controls__input {
    margin: 0;
  }

  .loader {
    width: 48px;
    height: 48px;
    display: inline-block;
    position: relative;
    transform: rotate(45deg);
  }
  .loader::before {
    content: '';
    box-sizing: border-box;
    width: 24px;
    height: 24px;
    position: absolute;
    left: 0;
    top: -24px;
    animation: animloader 4s ease infinite;
  }
  .loader::after {
    content: '';
    box-sizing: border-box;
    position: absolute;
    left: 0;
    top: 0;
    width: 24px;
    height: 24px;
    background: var(--v-primary-base);
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.15);
    animation: animloader2 2s ease infinite;
  }

  @keyframes animloader {
    0% {
      box-shadow: 0 24px rgba(255, 255, 255, 0), 24px 24px rgba(255, 255, 255, 0), 24px 48px rgba(255, 255, 255, 0), 0px 48px rgba(255, 255, 255, 0);
    }
    12% {
      box-shadow: 0 24px var(--v-primary-base), 24px 24px rgba(255, 255, 255, 0), 24px 48px rgba(255, 255, 255, 0), 0px 48px rgba(255, 255, 255, 0);
    }
    25% {
      box-shadow: 0 24px var(--v-primary-base), 24px 24px var(--v-primary-base), 24px 48px rgba(255, 255, 255, 0), 0px 48px rgba(255, 255, 255, 0);
    }
    37% {
      box-shadow: 0 24px var(--v-primary-base), 24px 24px var(--v-primary-base), 24px 48px var(--v-primary-base), 0px 48px rgba(255, 255, 255, 0);
    }
    50% {
      box-shadow: 0 24px var(--v-primary-base), 24px 24px var(--v-primary-base), 24px 48px var(--v-primary-base), 0px 48px var(--v-primary-base);
    }
    62% {
      box-shadow: 0 24px rgba(255, 255, 255, 0), 24px 24px var(--v-primary-base), 24px 48px var(--v-primary-base), 0px 48px var(--v-primary-base);
    }
    75% {
      box-shadow: 0 24px rgba(255, 255, 255, 0), 24px 24px rgba(255, 255, 255, 0), 24px 48px var(--v-primary-base), 0px 48px var(--v-primary-base);
    }
    87% {
      box-shadow: 0 24px rgba(255, 255, 255, 0), 24px 24px rgba(255, 255, 255, 0), 24px 48px rgba(255, 255, 255, 0), 0px 48px var(--v-primary-base);
    }
    100% {
      box-shadow: 0 24px rgba(255, 255, 255, 0), 24px 24px rgba(255, 255, 255, 0), 24px 48px rgba(255, 255, 255, 0), 0px 48px rgba(255, 255, 255, 0);
    }
  }

  @keyframes animloader2 {
    0% {
      transform: translate(0, 0) rotateX(0) rotateY(0);
    }
    25% {
      transform: translate(100%, 0) rotateX(0) rotateY(180deg);
    }
    50% {
      transform: translate(100%, 100%) rotateX(-180deg) rotateY(180deg);
    }
    75% {
      transform: translate(0, 100%) rotateX(-180deg) rotateY(360deg);
    }
    100% {
      transform: translate(0, 0) rotateX(0) rotateY(360deg);
    }
  }


</style>
